import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import * as Util from '../../../common/utilities/';
import { withSession } from '../../../i18n/containers/AppInit.js';

import {
  AttributeEditModal,
  DataExtensionList,
  ImportSchedule,
  ProfileAttributeTable,
  LocationAttributeMapping
} from '../';
import {
  Wizard,
  WizardPage,
  DuelingPicklist,
  EthicalScan
} from '../../../common/components';
import { ScopedNotification } from '@salesforce/design-system-react';
import { FtpInfo, NotificationInfo } from '../';

import './ImportProfileWizard.scss';
import { trimAndStrip } from '../../../utilities/stringUtils';

class ImportProfileWizard extends Component {
  modalName() {
    return 'profileImport';
  }

  closeModal() {
    this.props.actions.closeModal(this.modalName());
  }

  editAllowed() {
    return this.inUseProfileAttributeNames().length === 0;
  }

  isFTP() {
    return this.props?.flags?.ftpImport || this.props?.flags?.flags?.ftpImport;
  }

  allowNumber() {
    return (
      this.props?.flags?.numberRules || this.props?.flags?.flags?.numberRules
    );
  }

  showLocationMapping() {
    return this.props.rules?.data?.rules?.settings?.weather;
  }

  inUseProfileAttributeNames() {
    const rules = this.props.rules?.data?.rules;

    if (rules) {
      let exclusionKeys = [];
      let classifierKeys = [];

      if (rules.exclusion) {
        exclusionKeys = Object.keys(rules.exclusion).map((key, index) => {
          return rules.exclusion[key].consumerKey;
        });
      }
      if (rules.classifier) {
        classifierKeys = Object.keys(rules.classifier).map((key, index) => {
          return rules.classifier[key].consumerKey;
        });
      }

      return [...exclusionKeys, ...classifierKeys];
    }
    return [];
  }

  // ===================================
  // =         Event Handlers          =
  // ===================================
  handleProfileAttributeSelection(selection) {
    // if they're trying to remove attributes used in rules, only update the completed
    // status for the initial load
    if (!this.isValidSelection(selection)) {
      const oldData = [
        ...this.props.modals.profileImport?.data?.step3?.selectedAttributes
      ];
      const newData = {
        step3: {
          selectedAttributes: oldData,
          completed: oldData?.some(attr => !attr.locked)
        }
      };
      this.props.actions.updateModal(this.modalName(), newData);
    } else if (
      this.locationMappingValid(
        this.currentSelectedAttributeNames(),
        this.currentLocationMapping()
      )
    ) {
      const newData = {
        step3: {
          selectedAttributes: [...selection],
          completed: selection?.some(attr => !attr.locked)
        },
        step5: {
          profileAttributes: this.selectedDataExtensionAttributes(selection)
        }
      };
      this.props.actions.updateModal(this.modalName(), newData);
    } else {
      const newData = {
        step3: {
          selectedAttributes: [...selection],
          completed: selection?.some(attr => !attr.locked)
        },
        step4: {
          locationMapping: {},
          completed: false
        },
        step5: {
          profileAttributes: this.selectedDataExtensionAttributes(selection)
        }
      };
      this.props.actions.updateModal(this.modalName(), newData);
    }
  }

  // can't remove attributes used in rules
  isValidSelection(selection) {
    const inUse = this.inUseProfileAttributeNames();
    const selectionNames = selection.map(s => s.name);
    const baddies = inUse.filter(a => !selectionNames.includes(a));
    return baddies.length === 0;
  }

  handleAttributeEdit(attribute) {
    const profileAttributes = [...this.currentProfileAttributes()];
    const idx = profileAttributes.findIndex(attr => attr.id === attribute.id);
    profileAttributes[idx] = attribute;

    // strip null or empty values
    if (attribute.values) {
      attribute.values = attribute.values.filter(
        value => value !== null && value.trim() !== ''
      );
    }

    this.props.actions.updateModal(this.modalName(), {
      step5: { profileAttributes }
    });
    this.props.actions.closeModal('attributeEdit');
  }

  handleFinish(flags, session) {
    const schedule = this.currentSchedule()?.importRecurring
      ? this.currentSchedule()
      : {};

    this.isFTP()
      ? this.props.actions.createFtpImport(
          this.currentDataExtension(),
          schedule,
          this.currentNotificationInfo(),
          this.currentFtpInfo(),
          this.currentProfileAttributes(),
          'false', // importNow
          { cToken: session.cToken }
        )
      : this.props.actions.createDataExtensionImport(
          this.currentDataExtension(),
          schedule,
          this.currentNotificationInfo(),
          this.currentProfileAttributes(),
          'true', // importNow
          { cToken: session.cToken }
        );

    if (this.showLocationMapping()) {
      this.props.actions.saveRules(
        { location_mapping: this.currentLocationMapping() },
        { ruleType: 'location_mapping', cToken: session.cToken }
      );
    }
    this.closeModal();
  }

  handleDataExtensionSelect(event, selection) {
    if (!this.editAllowed()) {
      return;
    }
    const dataExtension = selection.selection[0];
    this.props.actions.getDataExtensionColumns(dataExtension);

    const nextSelection = {
      step1: {
        dataExtension,
        completed: dataExtension !== {}
      },
      step2: { completed: true },
      step3: {},
      step4: {},
      step5: {}
    };
    this.props.actions.updateModal(this.modalName(), nextSelection);
  }

  setStep1Complete() {
    if (this.props.modals.profileImport?.data?.step1) {
      const { step1 } = this.props.modals.profileImport.data;
      if (step1.dataExtension && !step1.completed) {
        this.props.actions.updateModal(this.modalName(), {
          step1: { ...step1, completed: true }
        });
      }
    }
  }

  handleScheduleChange(data) {
    this.props.actions.updateModal(this.modalName(), {
      step2: {
        completed: this.validNotifications(this.currentNotificationInfo()),
        notification_info: this.currentNotificationInfo(),
        schedule: { ...this.currentSchedule(), ...data }
      }
    });
  }

  handleFtpInfoChange(ftpFilename) {
    this.props.actions.updateModal(this.modalName(), {
      step2: {
        completed: this.validNotifications(this.currentNotificationInfo()),
        notification_info: this.currentNotificationInfo(),
        schedule: this.currentSchedule(),
        file_name: ftpFilename
      }
    });
  }

  handleNotificationEmailChange(email) {
    const newNotificationInfo = {
      ...this.currentNotificationInfo(),
      email: email.map(e => e.id)
    };

    this.props.actions.updateModal(this.modalName(), {
      step2: {
        completed: this.validNotifications(newNotificationInfo),
        notification_info: newNotificationInfo,
        schedule: this.currentSchedule()
      }
    });
  }

  handleNotifyOnSuccessToggle(notifyOnSuccess) {
    const newNotificationInfo = {
      ...this.currentNotificationInfo(),
      notify_on_success: notifyOnSuccess
    };

    this.props.actions.updateModal(this.modalName(), {
      step2: {
        completed: this.validNotifications(newNotificationInfo),
        notification_info: newNotificationInfo,
        schedule: this.currentSchedule()
      }
    });
  }

  handleNotifyOnErrorToggle(notifyOnError) {
    const newNotificationInfo = {
      ...this.currentNotificationInfo(),
      notify_on_error: notifyOnError
    };

    this.props.actions.updateModal(this.modalName(), {
      step2: {
        completed: this.validNotifications(newNotificationInfo),
        notification_info: newNotificationInfo,
        schedule: this.currentSchedule()
      }
    });
  }

  handleAttributeAction(action, attribute) {
    if (action === 'edit') {
      if (!attribute.values?.length) {
        attribute.values = [''];
      }
      if (attribute.data_type?.toLowerCase() === 'boolean') {
        attribute.editDisabled = true;
      }
      this.props.actions.openModal('attributeEdit', {
        attribute,
        original: {
          ...attribute,
          values: [...attribute.values]
        }
      });
    }
  }

  handleLocationMappingChange(changes) {
    const mapping = { ...this.currentLocationMapping() };
    Object.keys(changes).forEach(key => {
      const value = changes[key];
      if (!value) {
        delete mapping[key];
      } else {
        mapping[key] = value;
      }
    });
    this.props.actions.updateModal(this.modalName(), {
      step4: {
        locationMapping: mapping,
        completed: this.locationMappingValid(
          this.currentSelectedAttributeNames(),
          mapping
        )
      }
    });
  }

  locationMappingValid(attributeNames, mapping) {
    // each mapping value must be one of the attributeNames, but no duplicates
    const set = new Set(
      Object.values(mapping).filter(name => attributeNames.includes(name))
    );
    const dupeOrMissingName = set.size !== Object.values(mapping).length;

    // need at least one category mapped to be able to determine weather
    const nothingMapped = set.size < 1;

    // need something besides just language mapped
    const onlyLanguageMapped = set.size === 1 && mapping.language;

    let missingLinkedAttribute =
      //postal_code requires country
      (mapping.postal_code && !(mapping.postal_code && mapping.country)) ||
      // latitude requires longitude and vice-versa
      ((mapping.latitude || mapping.longitude) &&
        !(mapping.latitude && mapping.longitude)) ||
      // city requires country -- state requires city and country
      ((mapping.city || mapping.state) && !(mapping.city && mapping.country)) ||
      // (so country requires city or postal_code)
      (mapping.country && !(mapping.postal_code || mapping.city));

    return !(
      nothingMapped ||
      dupeOrMissingName ||
      missingLinkedAttribute ||
      onlyLanguageMapped
    );
  }

  // ===================================
  // =          Current Data           =
  // ===================================
  currentDataExtension() {
    return this.props?.modals?.profileImport?.data?.step1?.dataExtension;
  }

  currentDataExtensionKey() {
    return this.currentDataExtension()?.customer_key;
  }

  currentFtpInfo() {
    const current = this.props?.modals?.profileImport?.data?.step2?.file_name;
    const existingConfig = this.props.importConfiguration?.data
      ?.import_configuration?.source_info?.file_name;
    if (current === undefined) {
      if (existingConfig === undefined) {
        return { file_name: '' };
      } else {
        return {
          file_name: existingConfig
        };
      }
    }
    return { file_name: current };
  }

  currentNotificationInfo() {
    return (
      this.props?.modals?.profileImport?.data?.step2?.notification_info || {}
    );
  }

  notificationEmails() {
    return this.enhanceEmails(this.currentNotificationInfo()?.email || []);
  }

  enhanceEmails(emails) {
    if (!Array.isArray(emails)) {
      return [];
    } else {
      return emails.map(function(email) {
        return { id: email, label: email };
      });
    }
  }

  currentProfileAttributes() {
    return (
      this.props.modals?.profileImport?.data?.step5?.profileAttributes || []
    );
  }

  currentDataExtensionColumn() {
    const dataExtension = this.currentDataExtension();
    return dataExtension && dataExtension.sendable_data_extension_field;
  }

  currentSchedule() {
    const schedule = this.props.modals?.profileImport?.data?.step2?.schedule;
    return schedule || {};
  }

  currentLocationMapping() {
    return (
      this.props.modals?.profileImport?.data?.step4?.locationMapping ||
      this.props.rules?.data?.rules?.location_mapping ||
      {}
    );
  }

  currentSelectedAttributeNames() {
    return (
      this.props.modals.profileImport?.data?.step3?.selectedAttributes || []
    ).map(item => item.name);
  }

  selectedDataExtensionAttributes(selectedAttributes) {
    const selectedFromPicklist = this.props?.modals?.profileImport?.data?.step3
      ?.selectedAttributes;
    let selectedProfileAttributes =
      selectedAttributes ||
      selectedFromPicklist ||
      this.lockedProfileAttributes();
    return this.enhanceProfileAttributes(selectedProfileAttributes);
  }

  // add id and name for the picklist
  enhanceProfileAttributes(attrs) {
    const subscriberKey = this.currentDataExtension()
      ?.sendable_data_extension_field?.name;

    return attrs.map(attr => ({
      id: `${attr.name}`,
      label: attr.name,
      locked: attr.name === subscriberKey,
      ...attr
    }));
  }

  validNotifications(info) {
    if (
      !(info.email && info.email.length >= 1) &&
      (info.notify_on_success || info.notify_on_error)
    ) {
      return false;
    }
    return true;
  }

  componentDidUpdate(prevProps) {
    const updates = {};
    // when we transition to step 3
    if (
      Util.propChanged(
        prevProps,
        this.props,
        'modals.profileImport.data.currentStep',
        'step3'
      )
    ) {
      // Complete step 3 automatically if we have at least one unlocked attribute already selected
      if (this.selectedDataExtensionAttributes().some(attr => !attr.locked)) {
        const step3 = this.props.modals.profileImport.data?.step3 || {};
        updates.step3 = { ...step3, completed: true };
      }
    }

    // ensure location mapping step completion marker is always accurate
    if (
      Util.propChanged(
        prevProps,
        this.props,
        'modals.profileImport.data.step3.selectedAttributes'
      )
    ) {
      const step4 = this.props.modals.profileImport.data?.step4 || {};
      updates.step4 = {
        ...step4,
        completed: this.locationMappingValid(
          this.currentSelectedAttributeNames(),
          this.currentLocationMapping()
        )
      };
    }

    if (Object.keys(updates).length > 0) {
      this.props.actions.updateModal(this.modalName(), updates);
    }
  }

  dataExtensionPage() {
    const tallClassName = this.editAllowed()
      ? 'tall-table-container'
      : undefined;
    return (
      <WizardPage
        key="step1"
        step="step1"
        required={this.mode() === 'full'}
        excludeFromProgress={this.mode() === 'short'}
        stepName="components.ecs.profiles.step1.progressIndicator"
        flex
        footer={['CANCEL', 'PROGRESS', 'NEXT']}
      >
        {this.editAllowed() ? null : (
          <ScopedNotification
            className="data-extension-notification"
            theme="light"
          >
            <p>
              <FormattedMessage
                id={
                  'components.ecs.advancedSettings.dataExtensionSync.selectionModal.changeNotification'
                }
              />
            </p>
          </ScopedNotification>
        )}
        <DataExtensionList
          className={tallClassName}
          actions={this.props.actions}
          modals={this.props.modals}
          dataExtensions={this.props.dataExtensions}
          selectedDataExtension={this.currentDataExtension()}
          onDataExtensionSelect={this.handleDataExtensionSelect.bind(this)}
          onInitialLoad={this.setStep1Complete.bind(this)}
          rowSelectionMode="radio"
        />
      </WizardPage>
    );
  }

  importSettingsPage() {
    const { flags } = this.props;
    return (
      <WizardPage
        key="step2"
        step="step2"
        stepName="components.ecs.profiles.step2.progressIndicator"
        flex
        required={this.mode() === 'full'}
        excludeFromProgress={this.mode() === 'short'}
        footer={['BACK', 'PROGRESS', 'NEXT']}
      >
        <div className="schedule-and-notifications">
          <div className="page-title-and-description">
            <h2>
              <FormattedMessage id="components.ecs.import.schedule.header" />
            </h2>
            <div>
              <FormattedMessage id="components.ecs.import.schedule.description" />
            </div>
          </div>

          <ImportSchedule
            formChange={this.handleScheduleChange.bind(this)}
            schedule={this.currentSchedule()}
          />
          <FtpInfo
            ftpFilename={this.currentFtpInfo()}
            handleFtpInfoChange={this.handleFtpInfoChange.bind(this)}
            flags={flags}
          />
          <NotificationInfo
            email={this.notificationEmails()}
            notifyOnError={this.currentNotificationInfo().notify_on_error}
            notifyOnSuccess={this.currentNotificationInfo().notify_on_success}
            handleNotificationEmailChange={this.handleNotificationEmailChange.bind(
              this
            )}
            handleNotifyOnSuccessToggle={this.handleNotifyOnSuccessToggle.bind(
              this
            )}
            handleNotifyOnErrorToggle={this.handleNotifyOnErrorToggle.bind(
              this
            )}
          />
        </div>
      </WizardPage>
    );
  }

  availableProfileAttributes() {
    const { details } = this.props.dataExtensions.data;
    const subscriberKey = this.currentDataExtension()
      ?.sendable_data_extension_field?.name;
    const allowed_data_types = this.allowNumber()
      ? ['locale', 'text', 'decimal', 'number']
      : ['locale', 'text', 'decimal'];
    return details?.column_detail
      ?.filter(
        attr =>
          attr.name === subscriberKey ||
          'boolean' === attr.field_type?.toLowerCase() ||
          (allowed_data_types.includes(attr.field_type?.toLowerCase()) &&
            (!attr.max_length || attr.max_length <= 100) &&
            attr.storage_type !== 'Encrypted')
      )
      .map(attr => {
        const values =
          attr.field_type?.toLowerCase() === 'boolean'
            ? ['True', 'False']
            : undefined;
        return {
          id: `${attr.name}`,
          label: attr.name,
          name: attr.name,
          data_type: attr.field_type?.toLowerCase(),
          locked: attr.name === subscriberKey,
          scan_result: attr.scan_result,
          values
        };
      });
  }

  lockedProfileAttributes() {
    const availableAttributes = this.availableProfileAttributes() || [];
    return availableAttributes.filter(attr => attr.locked);
  }

  handleRenderEthicalScan(option) {
    return <EthicalScan {...option.scan_result} />;
  }

  profileAttributeSelectionPage() {
    const { details } = this.props.dataExtensions.data;

    const availableAttributes = this.availableProfileAttributes();
    const selectedAttributes = this.selectedDataExtensionAttributes();
    const isLoading =
      details == null || this.props.dataExtensions?.isLoadingDetails;

    const footer = ['BACK', 'PROGRESS', 'NEXT'];
    if (this.mode() === 'short') {
      footer[0] = 'CANCEL';
    }

    return (
      <WizardPage
        key="step3"
        step="step3"
        stepName="components.ecs.profiles.step3.progressIndicator"
        flex
        required
        footer={footer}
      >
        <div className="profile-attributes-page">
          <ScopedNotification theme="light">
            <p>
              <FormattedMessage id="components.ecs.profiles.step3.dataTypeNotification" />
            </p>
            <p>
              <a
                href="https://help.salesforce.com/articleView?id=mc_pb_sync_your_consumer_profile_data_extension.htm&type=5"
                rel="noopener noreferrer"
                target="_blank"
              >
                <FormattedMessage id="components.ecs.profiles.step3.learnMore" />
              </a>
            </p>
          </ScopedNotification>
          {this.inUseProfileAttributeNames().length === 0 ? null : (
            <ScopedNotification theme="light">
              <p>
                <FormattedMessage
                  id={
                    'components.ecs.advancedSettings.dataExtensionSync.selectionModal.changeAttributeNotification'
                  }
                />
              </p>
            </ScopedNotification>
          )}
          <div className="page-title-and-description">
            <h2>
              <FormattedMessage id="components.ecs.profiles.step3.header" />
            </h2>
            <div>
              <FormattedMessage
                id="components.ecs.profiles.step3.description"
                values={{ count: this.props.maxAttributes }}
              />
            </div>
          </div>
          <div className="picklist-container">
            <DuelingPicklist
              maxSelectable={this.props.maxAttributes}
              isLoading={isLoading}
              options={availableAttributes || []}
              selectedOptions={selectedAttributes}
              arrangeLockedItemsFirst={true}
              labelIds={{
                heading: 'components.ecs.profiles.step3.picklist.heading',
                left: 'components.ecs.profiles.step3.picklist.left',
                right: 'components.ecs.profiles.step3.picklist.right'
              }}
              onRenderMenuItem={this.handleRenderEthicalScan.bind(this)}
              onSelect={this.handleProfileAttributeSelection.bind(this)}
            />
          </div>
        </div>
      </WizardPage>
    );
  }

  profileLocationAttributeMappingPage() {
    const attributes = this.currentSelectedAttributeNames();
    const helpLink =
      'https://help.salesforce.com/s/articleView?id=sf.mc_pb_manage_profile_attributes.htm&type=5';
    return (
      <WizardPage
        key="step4"
        step="step4"
        stepName="components.ecs.profiles.step4.progressIndicator"
        required
        flex
        footer={['BACK', 'PROGRESS', 'NEXT']}
      >
        <div className="location-attribute-mapping">
          <div className="page-title-and-description">
            <h2>
              <FormattedMessage id="components.ecs.profiles.step4.header" />
            </h2>
            <div>
              <FormattedMessage
                id="components.ecs.profiles.step4.description"
                values={{
                  link: (
                    <a
                      rel="noopener noreferrer"
                      target="_blank"
                      href={helpLink}
                    >
                      {this.props.intl.formatMessage({
                        id: 'components.ecs.nocatalog.helpLinkText'
                      })}
                    </a>
                  )
                }}
              />
            </div>
          </div>
        </div>
        <LocationAttributeMapping
          attributes={attributes}
          mapping={this.currentLocationMapping()}
          onMappingChange={this.handleLocationMappingChange.bind(this)}
        />
      </WizardPage>
    );
  }

  profileAttributeSummaryPage() {
    const attributes = this.currentProfileAttributes().map(item => ({
      ...item,
      values: [...(item.values?.length ? trimAndStrip(item.values) : [])]
    }));

    const columns = {
      name: { sortable: false },
      hasValues: { sortable: false },
      dataType: { sortable: false },
      edit: { sortable: false }
    };

    return (
      <WizardPage
        key="step5"
        step="step5"
        stepName="components.ecs.profiles.step5.progressIndicator"
        flex
        footer={['BACK', 'PROGRESS', 'FINISH']}
      >
        <div className="attribute-table">
          <div className="page-title-and-description">
            <h2>
              <FormattedMessage id="components.ecs.profiles.step5.header" />
            </h2>
            <div>
              <FormattedMessage id="components.ecs.profiles.step5.description" />
            </div>
          </div>
          <ProfileAttributeTable
            attributes={attributes}
            lockedAttributes={this.lockedProfileAttributes()}
            columns={columns}
            isReadonly={false}
            onListAction={this.handleAttributeAction.bind(this)}
          />
        </div>
      </WizardPage>
    );
  }

  renderWizardPages() {
    const pages = [
      this.dataExtensionPage(),
      this.importSettingsPage(),
      this.profileAttributeSelectionPage(),
      this.profileAttributeSummaryPage()
    ];

    if (this.showLocationMapping()) {
      pages.splice(3, 0, this.profileLocationAttributeMappingPage());
    }

    return pages;
  }

  mode() {
    return this.props.mode || 'full';
  }

  render() {
    const { formatMessage } = this.props.intl;
    const { flags } = this.props;

    return withSession(session => {
      return (
        <React.Fragment>
          <Wizard
            containerClassName="import-profile-wizard"
            title={formatMessage({
              id: 'components.ecs.profiles.modal.manualImport'
            })}
            modalName="profileImport"
            size="small"
            onFinish={this.handleFinish.bind(this, flags, session)}
            {...this.props}
          >
            {this.renderWizardPages()}
          </Wizard>
          <AttributeEditModal
            actions={this.props.actions}
            modals={this.props.modals}
            attributeType="profile"
            onSave={this.handleAttributeEdit.bind(this)}
          />
        </React.Fragment>
      );
    });
  }
}

ImportProfileWizard.propTypes = {
  actions: PropTypes.shape({
    createDataExtensionImport: PropTypes.func.isRequired,
    saveRules: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,
    updateModal: PropTypes.func.isRequired
  }).isRequired,
  mode: PropTypes.oneOf(['full', 'short']),
  dataExtensions: PropTypes.object.isRequired,
  modals: PropTypes.shape({
    profileImport: PropTypes.shape({
      isOpen: PropTypes.bool.isRequired,
      data: PropTypes.shape({
        code: PropTypes.string,
        details: PropTypes.array
      })
    })
  }).isRequired,
  intl: intlShape.isRequired,
  rules: PropTypes.object,
  flags: PropTypes.object.isRequired,
  maxAttributes: PropTypes.number.isRequired
};

export default injectIntl(ImportProfileWizard);
